import { useEffect } from "react";
import { useNavigate } from "react-router";
import { HomeScreen, type HomeState } from "../../screens/home/HomeScreen";
import { ScreenState } from "../../components/ScreenState";
import { TabBar } from "../../components/TabBar";
import { useCachedAsync } from "../../lib/useCachedAsync";
import { OfflineNote } from "../../components/OfflineNote";
import { ExamSwitcher } from "../../components/ExamSwitcher";
import { fetchMe } from "../../api/me";
import { updateProfile } from "../../api/profile";
import { flushPendingInstall } from "../../lib/installPrompt";

/**
 * Home, from the API.
 *
 * It was rendering `DEMO_HOME_SUBJECTS` — real-looking scores for a student who
 * had never practised. That is worse than an empty state: it tells someone
 * something untrue about themselves, which is the one thing the readiness model
 * is built not to do.
 */
export function HomeRoute() {
  const navigate = useNavigate();
  // Saved on the phone, so Home opens offline with the last readiness it showed.
  const { data, loading, error, reload, staleSince } = useCachedAsync("me", fetchMe);

  // Signed in with a student profile: send an install parked while signed out,
  // or record a launch from the home screen icon (iOS gives no other signal).
  useEffect(() => {
    if (data) void flushPendingInstall();
  }, [data]);

  const handleSaveName = async (firstName: string) => {
    await updateProfile({ firstName });
    reload();
  };

  return (
    <ScreenState
      loading={loading}
      error={error}
      onRetry={reload}
      footer={<TabBar />}
      header={<ExamSwitcher exams={data?.exams} active={data?.activeExam} />}
    >
      <OfflineNote since={staleSince} />
      {data ? (
        <HomeScreen
          firstName={data.student.firstName ?? "there"}
          streakDays={data.student.streakDays}
          state={homeStateFor(data)}
          trialUsed={data.trial.used}
          trialTotal={data.trial.limit}
          subjects={data.subjects.map((subject) => ({
            subject: subject.name,
            readiness: subject.readiness,
            confidence: subject.confidence,
            weakTopics: subject.weakTopics,
          }))}
          onUpgrade={() => void navigate("/upgrade")}
          onSaveName={handleSaveName}
        />
      ) : null}
    </ScreenState>
  );
}

/**
 * Which of the five Home states applies.
 *
 * Derived from the API rather than passed by whoever renders the screen — a
 * client deciding it is "locked" independently of the entitlement it was told
 * about is how a paying student ends up looking at a paywall.
 */
function homeStateFor(data: {
  entitlement: { hasFullAccess: boolean };
  trial: { exhausted: boolean };
  readiness: { overall: number | null };
  subjects: { readiness: number | null }[];
}): HomeState {
  if (!data.entitlement.hasFullAccess && data.trial.exhausted) return "locked";

  const hasAnyScore =
    data.readiness.overall !== null || data.subjects.some((s) => s.readiness !== null);
  if (!hasAnyScore) return "cold-start";

  if (!data.entitlement.hasFullAccess) return "trial";
  return "normal";
}
